import React from "react";

type Props = {
  email: string | null | undefined;
  visible: boolean;
};

// Per-buyer watermark for paid chapters. Goals (in order of importance):
//   1. Deter casual screenshot-and-share — most people won't redistribute
//      a screenshot stamped with their email visible to all recipients.
//   2. Trace deliberate leaks. If a "ripped" copy appears on Telegram or
//      WhatsApp, the watermarked email in the screenshot fingerprints
//      the source account.
//   3. Stay readable. Tile density and opacity tuned to be unobtrusive
//      on reading but unambiguous in any cropped screenshot.
//
// Implementation notes:
//   - pointer-events: none so clicks/scroll pass through to content.
//   - position: fixed + z-index 1000 — sits above content but below the
//     paywall modal (z 10000) and TOC drawer (z 60).
//   - aria-hidden so screen readers don't read the email a thousand times.
//   - Tiles use CSS rotate(-30deg) and a repeated background image of
//     the text, rendered via a data: SVG so we don't need to ship a font
//     or rely on canvas APIs. SVG inherits the page's font-family.
//   - The free chapter 1 doesn't show this (`visible=false`) so visitors
//     never see a watermarked preview.
//
// Threat model out of scope:
//   - DevTools removal of the overlay div (trivially possible). Our
//     answer to that is item 1.1 — moving content out of the bundle so
//     DOM access still requires server-side auth.
//   - Faking auth state to remove email. Auth is enforced by the paywall
//     gate above this; only paid users render this component at all.

export function Watermark({ email, visible }: Props): React.ReactElement | null {
  if (!visible || !email) return null;

  // SVG-based repeating tile. Encoding the email twice per tile (once
  // bold, once italic-ish via skew) makes the pattern hard to miss in
  // any reasonable screenshot crop, even at low opacity.
  const safe = email.replace(/[<>"&]/g, "");
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='420' height='240' viewBox='0 0 420 240'>` +
    `<style>text{font-family:'Heebo','Arial',sans-serif;font-size:14px;fill:rgba(92,61,46,0.10);font-weight:500}</style>` +
    `<text x='10' y='40' transform='rotate(-30 10 40)'>${safe} · eiforall.com</text>` +
    `<text x='10' y='160' transform='rotate(-30 10 160)'>${safe} · eiforall.com</text>` +
    `</svg>`;
  const bg = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1000,
        backgroundImage: bg,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        // Slight mix-blend so the watermark sits naturally over both the
        // cream page background and dark accent panels.
        mixBlendMode: "multiply",
      }}
    />
  );
}
